/**
 * Blok core: 8x8 block-blast board + a hidden picture underneath.
 *
 * Rules (all deterministic; the same level + the same move list always gives the same state):
 *  - 3 pieces are dealt at a time; place all 3 and 3 new ones are dealt. No rotation.
 *  - A piece snaps only if every one of its cells is free.
 *  - After a placement every full row AND full column clears at the same time.
 *  - Lines in one placement = combo; consecutive placements that clear = streak (multiplier).
 *  - The picture block under a cell is revealed the first time that cell is CLEARED
 *    (covering a block with a piece reveals nothing; clearing it does).
 *  - Game over when none of the remaining pieces fits anywhere. Retry is free (CLAUDE.md H2/H4).
 *  - Guess any time: correct = level won (the shareable number is `placed`, fewer is better);
 *    wrong = -points and the answer goes to the tried list.
 */
import { hashSeed, matchesAnswer, normalizeAnswer, Rng, type Picture } from "@enjoyment/core";
import { SIDE, piece, PIECE_IDS, type PieceDef } from "./pieces";
import { anyPlacement, boardToMasks, clearLines, fits, fullLines, place, type Masks } from "./masks";

export const CELLS = SIDE * SIDE;
export const TRAY = 3;
/** Board value for a pre-filled obstacle cell (any other non-zero value = piece color + 1). */
export const OBSTACLE = 9;

export const WRONG_GUESS_PENALTY = 100;
/** Shareable ranking value: pieces placed + this many per wrong guess (brute-force guessing is not free). */
export const WRONG_GUESS_PIECES = 2;
export const PERFECT_CLEAR_BONUS = 150;

export interface BlokLevel {
  n: number;
  seed: number;
  theme: string;
  /** How many times this theme came up before (picks the k-th picture of the theme's shuffled order). */
  pictureIndex: number;
  /** piece id -> relative weight in the bag (absent / 0 = never dealt). */
  weights: Record<string, number>;
  /** Pre-filled obstacle cells (indices 0..63). */
  obstacles: number[];
  /** Deals try this many times to find a trio that can ALL be placed on the current board. */
  fairTries: number;
}

export type Status = "playing" | "won" | "over";

export interface BlokState {
  level: BlokLevel;
  picture: Picture;
  /** CELLS entries: 0 empty, 1..8 = piece color + 1, OBSTACLE = obstacle. */
  board: number[];
  /** CELLS entries: has the picture block under this cell been revealed? */
  revealed: boolean[];
  tray: (string | null)[];
  dealIndex: number;
  placed: number;
  score: number;
  streak: number;
  bestStreak: number;
  bestCombo: number;
  lines: number;
  wrong: number;
  wrongLabels: string[];
  status: Status;
  /** Pieces placed at the moment of the winning guess (0 until won). */
  solvedAt: number;
}

export type Move = { kind: "place"; slot: number; r: number; c: number } | { kind: "guess"; text: string };

export interface PlaceResult {
  slot: number;
  piece: PieceDef;
  /** Board indices the piece now occupies. */
  cells: number[];
  rows: number[];
  cols: number[];
  /** Board indices cleared by this placement (union of rows and cols). */
  cleared: number[];
  /** Cleared cells whose picture block was revealed for the first time. */
  newlyRevealed: number[];
  lines: number;
  points: number;
  streak: number;
  perfect: boolean;
  dealt: boolean;
  status: Status;
}

// ---------------------------------------------------------------- scoring

/** 1 line = 10, then triangular: 2 = 30, 3 = 60, 4 = 100, ... Multiplied by the streak. */
export function lineBase(lines: number): number {
  return (10 * lines * (lines + 1)) / 2;
}

/** Streak 1 = x1, 2 = x1.5, 3 = x2 ... capped at x3. */
export function streakMultiplier(streak: number): number {
  return Math.min(3, 1 + 0.5 * Math.max(0, streak - 1));
}

// ---------------------------------------------------------------- dealing

function weightedPick(rng: Rng, ids: readonly string[], weights: Record<string, number>): string {
  let total = 0;
  for (const id of ids) total += weights[id] ?? 0;
  if (total <= 0) return ids[0]!;
  let x = rng.next() * total;
  for (const id of ids) {
    x -= weights[id] ?? 0;
    if (x < 0) return id;
  }
  return ids[ids.length - 1]!;
}

/** Can ALL pieces be placed one after another (any order, clears in between)? Node-budgeted DFS. */
export function trioSolvable(m: Masks, ids: readonly string[], budget = 6000): boolean {
  let nodes = 0;
  const rec = (cur: Masks, left: string[]): boolean => {
    if (!left.length) return true;
    const tried = new Set<string>();
    for (let i = 0; i < left.length; i++) {
      const id = left[i]!;
      if (tried.has(id)) continue;
      tried.add(id);
      const p = piece(id);
      const rest = left.filter((_, k) => k !== i);
      for (let r = 0; r + p.h <= SIDE; r++)
        for (let c = 0; c + p.w <= SIDE; c++) {
          if (!fits(cur, p, r, c)) continue;
          if (++nodes > budget) return true; // out of budget: assume playable
          const next = cur.slice();
          place(next, p, r, c);
          clearLines(next);
          if (rec(next, rest)) return true;
        }
    }
    return false;
  };
  return rec(m, [...ids]);
}

/**
 * Deal 3 pieces. A pure function of (level, dealIndex, board): deterministic and replayable.
 * Early/friendly levels insist on a trio that can be fully placed (no unwinnable early deals);
 * every level insists on at least one piece fitting. Tries are seeded, so it is reproducible.
 */
export function dealPieces(level: BlokLevel, board: readonly number[], dealIndex: number): string[] {
  const ids = PIECE_IDS.filter((id) => (level.weights[id] ?? 0) > 0);
  const m = boardToMasks(board);
  let anyFit: string[] | null = null;
  let last: string[] = [];
  const tries = level.fairTries + 12;
  for (let attempt = 0; attempt < tries; attempt++) {
    const rng = new Rng(hashSeed(`blok:${level.seed}:${dealIndex}:${attempt}`));
    const trio = [0, 1, 2].map(() => weightedPick(rng, ids, level.weights));
    last = trio;
    if (trio[0] === trio[1] && trio[1] === trio[2] && attempt < tries - 1) continue; // no triplets
    if (!trio.some((id) => anyPlacement(m, piece(id)))) continue;
    anyFit ??= trio;
    if (attempt < level.fairTries) {
      if (trioSolvable(m, trio)) return trio;
    } else return anyFit;
  }
  return anyFit ?? last;
}

// ---------------------------------------------------------------- state

export function createGame(level: BlokLevel, picture: Picture): BlokState {
  const board = new Array<number>(CELLS).fill(0);
  for (const i of level.obstacles) board[i] = OBSTACLE;
  const s: BlokState = {
    level,
    picture,
    board,
    revealed: new Array<boolean>(CELLS).fill(false),
    tray: dealPieces(level, board, 0),
    dealIndex: 0,
    placed: 0,
    score: 0,
    streak: 0,
    bestStreak: 0,
    bestCombo: 0,
    lines: 0,
    wrong: 0,
    wrongLabels: [],
    status: "playing",
    solvedAt: 0,
  };
  s.status = anyPieceFits(s) ? "playing" : "over";
  return s;
}

export function masksOf(s: BlokState): Masks {
  return boardToMasks(s.board);
}

export function canPlaceAt(s: BlokState, slot: number, r: number, c: number): boolean {
  const id = s.tray[slot];
  if (s.status !== "playing" || !id) return false;
  return fits(masksOf(s), piece(id), r, c);
}

export function anyPieceFits(s: BlokState): boolean {
  const m = masksOf(s);
  return s.tray.some((id) => id !== null && anyPlacement(m, piece(id)));
}

export function revealedCount(s: BlokState): number {
  let n = 0;
  for (const r of s.revealed) if (r) n++;
  return n;
}
export function revealedFraction(s: BlokState): number {
  return revealedCount(s) / CELLS;
}
/** Map stars: name the picture while little of it shows, without wrong answers. */
export function starsFor(s: BlokState): 1 | 2 | 3 {
  const shown = revealedFraction(s);
  if (s.wrong === 0 && shown <= 0.4) return 3;
  if (s.wrong <= 1 && shown <= 0.65) return 2;
  return 1;
}
export function filledCells(s: BlokState): number {
  let n = 0;
  for (const v of s.board) if (v) n++;
  return n;
}

/** Place tray piece `slot` with its top-left cell at (r, c). Returns null if illegal. */
export function placePiece(s: BlokState, slot: number, r: number, c: number): PlaceResult | null {
  if (!canPlaceAt(s, slot, r, c)) return null;
  const p = piece(s.tray[slot]!);
  const cells: number[] = [];
  for (const [dr, dc] of p.cells) {
    const i = (r + dr) * SIDE + (c + dc);
    s.board[i] = p.color + 1;
    cells.push(i);
  }
  // Clear every full row and column at once (detected on the board with the piece already in).
  const m = masksOf(s);
  const { rows, cols } = fullLines(m);
  const clearedSet = new Set<number>();
  for (const rr of rows) for (let cc = 0; cc < SIDE; cc++) clearedSet.add(rr * SIDE + cc);
  for (const cc of cols) for (let rr = 0; rr < SIDE; rr++) clearedSet.add(rr * SIDE + cc);
  const cleared = [...clearedSet].sort((a, b) => a - b);
  const newlyRevealed: number[] = [];
  for (const i of cleared) {
    s.board[i] = 0;
    if (!s.revealed[i]) {
      s.revealed[i] = true;
      newlyRevealed.push(i);
    }
  }
  const lines = rows.length + cols.length;
  s.tray[slot] = null;
  s.placed++;
  s.lines += lines;
  let points = p.size;
  s.streak = lines > 0 ? s.streak + 1 : 0;
  s.bestStreak = Math.max(s.bestStreak, s.streak);
  s.bestCombo = Math.max(s.bestCombo, lines);
  const perfect = lines > 0 && s.board.every((v) => v === 0);
  if (lines > 0) points += Math.round(lineBase(lines) * streakMultiplier(s.streak));
  if (perfect) points += PERFECT_CLEAR_BONUS;
  s.score += points;

  let dealt = false;
  if (s.tray.every((t) => t === null)) {
    s.dealIndex++;
    s.tray = dealPieces(s.level, s.board, s.dealIndex);
    dealt = true;
  }
  if (!anyPieceFits(s)) s.status = "over";
  return { slot, piece: p, cells, rows, cols, cleared, newlyRevealed, lines, points, streak: s.streak, perfect, dealt, status: s.status };
}

export interface GuessOutcome {
  correct: boolean;
  /** Same wrong answer typed again: echoed back, costs nothing. */
  repeat: boolean;
  /** Points lost. */
  penalty: number;
}

export function guessAnswer(s: BlokState, text: string): GuessOutcome {
  if (s.status !== "playing") return { correct: false, repeat: false, penalty: 0 };
  if (matchesAnswer(s.picture, text)) {
    s.status = "won";
    s.solvedAt = s.placed;
    return { correct: true, repeat: false, penalty: 0 };
  }
  const n = normalizeAnswer(text);
  if (!n || s.wrongLabels.some((w) => normalizeAnswer(w) === n)) return { correct: false, repeat: true, penalty: 0 };
  s.wrong++;
  s.wrongLabels.push(text.trim());
  const penalty = Math.min(s.score, WRONG_GUESS_PENALTY);
  s.score -= penalty;
  return { correct: false, repeat: false, penalty };
}

/** The ranking value: lower is better. */
export function resultPieces(s: BlokState): number {
  return s.solvedAt + s.wrong * WRONG_GUESS_PIECES;
}

/** Re-run a recorded game. Illegal moves are ignored (a tampered log cannot cheat the core). */
export function replay(level: BlokLevel, picture: Picture, moves: readonly Move[]): BlokState {
  const s = createGame(level, picture);
  for (const m of moves) {
    if (m.kind === "place") placePiece(s, m.slot, m.r, m.c);
    else guessAnswer(s, m.text);
  }
  return s;
}

// ---------------------------------------------------------------- share

/** Wordle-style 8x8 grid of revealed blocks. Spoils nothing: only WHERE you looked. */
export function shareGrid(s: BlokState, on = "🟩", off = "⬛"): string {
  const rows: string[] = [];
  for (let r = 0; r < SIDE; r++) {
    let line = "";
    for (let c = 0; c < SIDE; c++) line += s.revealed[r * SIDE + c] ? on : off;
    rows.push(line);
  }
  return rows.join("\n");
}
